import { CommonModule } from '@angular/common';
import {
  Component,
  DestroyRef,
  EventEmitter,
  forwardRef,
  inject,
  Input,
  OnInit,
  output,
  Output,
  signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  ControlValueAccessor,
  FormsModule,
  NG_VALUE_ACCESSOR,
  ReactiveFormsModule,
} from '@angular/forms';
import { TooltipComponent } from '@app/components/tooltip';
import { Spacing } from '@app/shared/types';
import { debounceTime, distinctUntilChanged, Subject } from 'rxjs';

export enum InputDateRestriction {
  NONE = 'none',
  TODAY_OR_FUTURE = 'todayOrFuture',
  FUTURE_ONLY = 'futureOnly',
}

export enum InputType {
  TEXT = 'text',
  PASSWORD = 'password',
  EMAIL = 'email',
  SEARCH = 'search',
  DATE = 'date',
  NUMBER = 'number',
}

export enum InputIcon {
  NONE = 'none',
  SEARCH = 'search',
}

export enum InputVariant {
  FILLED = 'filled', //contained
  OUTLINE = 'outline',
}

export enum InputDirection {
  HORIZONTAL = 'horizontal',
  VERTICAL = 'vertical',
}

export enum InputSize {
  SMALL = 'small',
  MEDIUM = 'medium',
  LARGE = 'large',
}

export enum InputWidth {
  FIXED = 'fixed',
  FIXED_DATE = 'fixed-date',
  FULL = 'full',
  FIXED_NUMBER = 'fixed-number',
}

export enum InputFieldSpacing {
  'NONE' = 0,
  'X_SMALL' = 8,
  'SMALL' = 16,
  'MEDIUM' = 24,
  'LARGE' = 32,
  'X_LARGE' = 40,
}
let nextId = 0;

@Component({
  standalone: true,
  selector: 'app-input',
  templateUrl: './input.component.html',
  styleUrls: ['./input.component.css'],
  imports: [CommonModule, FormsModule, ReactiveFormsModule, TooltipComponent],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => InputComponent),
      multi: true,
    },
  ],
})
export class InputComponent implements ControlValueAccessor, OnInit {
  @Input() id = `input-${nextId++}`;
  @Input() info!: string;
  @Input() gap = Spacing.SMALL;
  @Input() type = InputType.TEXT;
  @Input() icon = InputIcon.NONE;
  @Input() size = InputSize.MEDIUM;
  @Input() width = InputWidth.FIXED;
  @Input() spacingBottom = InputFieldSpacing.NONE;
  @Input() direction = InputDirection.HORIZONTAL;
  @Input() variant = InputVariant.FILLED;
  @Input() placeholder = '';
  @Input() testId!: string;
  @Input() errorMessage!: string;
  @Input() error = false;
  @Input() inputValue = signal('');
  @Input() required = false;
  @Input() set valueData(value: string) {
    this.inputValue.set(value);
    this.value = value;
  }
  @Input() disabled = false;
  @Input() multiLine = false;
  @Input() set labelData(value: string) {
    this.hasLabel = !!value;
    this.label = value;
  }
  @Input() maxLength = 1000;
  @Input() debounce = 500; // in ms, default to .5 sec
  @Input() list!: string;
  @Input() listData!: [];
  @Input() dateRestriction = InputDateRestriction.TODAY_OR_FUTURE;
  @Input() min?: number;
  @Input() max?: number;

  signalValueChanged = output<string>();
  hasLabel: boolean;
  showPassword = false;
  showTogglePasswordIcon = false;
  label!: string;
  padding: string;
  showTooltip = false;
  minDate: string | null = null;
  value: string;

  @Output()
  valueChanged = new EventEmitter<string>();

  @Output()
  inputSearch = new EventEmitter<string>();

  @Output() inputBlur = new EventEmitter<string>();

  @Output() typeahead = new EventEmitter<string>();

  @Output() inputChange = new EventEmitter<string>();

  typeaheadSubject = new Subject<string>();

  InputType = InputType;
  InputIcon = InputIcon;

  private readonly _destroyRef = inject(DestroyRef);

  togglePassword() {
    this.showPassword = !this.showPassword;
    if (this.showPassword) {
      this.type = InputType.TEXT;
    } else {
      this.type = InputType.PASSWORD;
    }
  }

  ngOnInit() {
    this.padding = this.icon === InputIcon.NONE ? '0 16px' : '0 32px';
    this.hasLabel = !!this.label;
    this.showTogglePasswordIcon = this.type === InputType.PASSWORD;
    this.typeaheadSubject
      .pipe(
        debounceTime(this.debounce),
        distinctUntilChanged(),
        takeUntilDestroyed(this._destroyRef)
      )
      .subscribe((value) => {
        this.typeahead.next(value);
      });

    if (this.type === InputType.NUMBER) {
      const DEFAULT_MIN = 1;
      const DEFAULT_MAX = 99;

      if (this.min == null && this.max == null) {
        this.min = DEFAULT_MIN;
        this.max = DEFAULT_MAX;
      } else if (this.max == null) {
        this.max = Math.max(this.min!, DEFAULT_MAX);
      } else if (this.min == null) {
        this.min = Math.min(this.max, DEFAULT_MIN);
      }
    }

    if (this.type === InputType.DATE) {
      let date: Date | null = null;
      if (this.dateRestriction === InputDateRestriction.FUTURE_ONLY) {
        date = new Date();
        date.setDate(date.getDate() + 1);
      } else if (
        this.dateRestriction === InputDateRestriction.TODAY_OR_FUTURE
      ) {
        date = new Date();
      }
      if (date) {
        this.minDate = this.formatDateForInput(date);
      }
    }
  }

  private formatDateForInput(date: Date): string {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }

  private onChange: (value: string) => void = () => {
    // placeholder for ControlValueAccessor
  };
  private onTouched: () => void = () => {
    // placeholder for ControlValueAccessor
  };

  writeValue(value: string): void {
    this.value = value;
    this.inputValue.set(value);
  }

  registerOnChange(fn: (value: string) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  onInputChange(event: Event) {
    this.handleValueChange(event);
  }

  onSearch(event: Event) {
    const newValue = (event.target as HTMLInputElement).value;
    this.emitChange(newValue, this.inputSearch);
  }

  onBlur(event: Event) {
    const newValue = (event.target as HTMLInputElement).value;
    this.emitChange(newValue, this.inputBlur);
  }

  change(event: Event) {
    const newValue = (event.target as HTMLInputElement).value;
    this.emitChange(newValue, this.inputChange);
  }

  focus(event: Event) {
    if (this.list) {
      const input = event.target as HTMLInputElement;
      input.value = null;
    }
  }

  private handleValueChange(event: Event): void {
    const newValue = (event.target as HTMLInputElement).value;
    this.value = newValue;
    this.inputValue.set(newValue);
    this.valueChanged.emit(newValue);
    this.signalValueChanged.emit(newValue);
    this.onChange(newValue);
    this.typeaheadSubject.next(newValue);
  }

  // (search) event fires twice sometimes.  Make sure only emit actual changed values
  private _lastValue?: string;

  private emitChange(value: string, emitter: EventEmitter<string>): void {
    if (this._lastValue !== value) {
      emitter.emit(value);
      this._lastValue = value;
    }
  }
}
